import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { IOffer } from "../../interfaces/IOffer";
import { IOfferPosition } from "../../interfaces/IOfferPosition";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { IOfferState } from "../../interfaces/IOfferState";
import { IProject } from "../../interfaces/IProject";
import { IUserSmall } from "../../interfaces/IUser";
import { IProjectState } from "../../interfaces/IProjectState";
import { SalesBoardUser } from "./SalesBoardUser";
import { useGetLogStatistic, useGetProjectSmall, useGetProjectState } from "../../hooks/data/data_project";
import CloseIcon from "@mui/icons-material/Close";
import { useGetOfferPositionSmall, useGetOfferSmall } from "../../hooks/data/data_offer";

export interface IChartData {
    name: string;
    value: number;
}

interface IProps {
    userSmallArray: IUserSmall[];
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#FF8042", "#FF8042", "#FF8042", "#FF8042", "#FF8042"];

export const SalesDasboard: React.FC<IProps> = ({ userSmallArray }) => {
    const { logArray, refetechLogArrayByProjectId, isPendingLogArray } = useGetLogStatistic();
    const { projectSmallArray, isLoadingProjectSmallArray, refetchProjectSmall } = useGetProjectSmall();
    const { projectStateArray, isLoadingProjectStateArray, refetchProjectState } = useGetProjectState();

    const [fromDate, setFromDate] = useState<Date | null>(() => {
        let date = new Date();
        return new Date(date.getFullYear(), date.getMonth(), 1, 3);
    });
    const [toDate, setToDate] = useState<Date | null>(() => {
        let date = new Date();
        return new Date(date.getFullYear(), date.getMonth() + 1, 0, 3);
    });
    const [selectedUser, setSelectedUser] = useState<number[]>(() => {
        const stored = localStorage.getItem("selectedUser");
        return stored ? JSON.parse(stored) : [Number(localStorage.getItem("idUser"))];
    });

    const [showTeamChart, setShowTeamChart] = useState<boolean>(() => {
        const stored = localStorage.getItem("showTeamChart");
        return stored === null ? true : stored === "true";
    });

    const [showLeadChart, setShowLeadChart] = useState<boolean>(() => {
        const stored = localStorage.getItem("showLeadChart");
        return stored === null ? true : stored === "true";
    });

    // const [projectSmallArray, setProjectSmallArray, statusCodeProjectArray] = useFetch<IProject[]>("/project/small/");

    const [dataArray, setDataArray] = useState<IChartData[]>([]);

    useEffect(() => {
        localStorage.setItem("selectedUser", JSON.stringify(selectedUser));
    }, [selectedUser]);

    if (userSmallArray === undefined || isLoadingProjectStateArray || isLoadingProjectSmallArray || isPendingLogArray) {
        return (
            <>
                Bitte warten...
                <br />
                <br />
                --- DEBUG ---
                <br />
                userSmallArray === undefined: {JSON.stringify(userSmallArray === undefined)}
                <br />
                isLoadingProjectStateArray: {JSON.stringify(isLoadingProjectStateArray)}
                <br />
                isLoadingProjectSmallArray: {JSON.stringify(isLoadingProjectSmallArray)}
                <br />
                isPendingLogArray: {JSON.stringify(isPendingLogArray)}
            </>
        );
    }

    if (!projectSmallArray || !projectStateArray) {
        return <>Es ist ein Fehler aufgetretten!</>;
    }

    // Einzigartige Leadquellen aus Backend filtern
    const uniqueLeadNames = Array.from(new Set(projectSmallArray.map((p) => p.leadQuelle ?? "unbekannt")));
    //IDs: Anfrage(1), B-Termin(3), Angebot(4), Bau(7 - "Laufend"), Rechnung(8 - "Abbrechnung")
    const statusesToInclude = [1, 3, 4, 7, 8];

    const multiSeriesData = statusesToInclude.map((stateId) => {
        const stateObj = projectStateArray.find((s) => s.id === stateId);
        const name = stateObj ? stateObj.projectState : stateId.toString();
        const entry: Record<string, any> = { name };
        uniqueLeadNames.forEach((lq) => {
            entry[lq] = projectSmallArray.filter(
                (p) => p.ProjectState === stateId && (p.leadQuelle ?? "Unbekannt") === lq,
            ).length;
        });
        return entry;
    });

    const handleRemoveUser = (userId: number) => {
        setSelectedUser((prev) => prev.filter((id) => id !== userId));
    };

    const handleHideTeamChart = () => {
        setShowTeamChart(false);
        localStorage.setItem("showTeamChart", "false");
    };

    const handleShowTeamChart = () => {
        setShowTeamChart(true);
        localStorage.setItem("showTeamChart", "true");
    };

    const handleTeamChartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            handleShowTeamChart();
        } else {
            handleHideTeamChart();
        }
    };

    //Lead-Quellen
    const handleHideLeadChart = () => {
        setShowLeadChart(false);
        localStorage.setItem("showLeadChart", "false");
    };
    const handleShowLeadChart = () => {
        setShowLeadChart(true);
        localStorage.setItem("showLeadChart", "true");
    };
    const handleLeadChartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) handleShowLeadChart();
        else handleHideLeadChart();
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h5">Vertrieb</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                        control={<Checkbox checked={showTeamChart} onChange={handleTeamChartChange} size="small" />}
                        label="Gesamtes Team anzeigen"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={showLeadChart} onChange={handleLeadChartChange} size="small" />}
                        label="Lead-Quellen anzeigen"
                    />
                </Box>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={3}>
                    <TextField
                        label="Von"
                        value={fromDate === null ? "" : fromDate.toISOString().split("T")[0]}
                        type="date"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setFromDate(event.target.value === "" ? null : new Date(event.target.value))
                        }
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        label="Bis"
                        value={toDate === null ? "" : toDate.toISOString().split("T")[0]}
                        type="date"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setToDate(event.target.value === "" ? null : new Date(event.target.value))
                        }
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel id="folder-label" shrink>
                            Mitarbeiter
                        </InputLabel>
                        <Select
                            labelId="folder-label"
                            placeholder="Mitarbeiter auswählen"
                            id="folder"
                            multiple
                            notched
                            label="Mitarbeiter"
                            value={selectedUser}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value && typeof value !== "string") {
                                    setSelectedUser(value);
                                }
                            }}
                            renderValue={(selectedValues) =>
                                selectedValues
                                    .map((selectedValue) => {
                                        let user = userSmallArray.find((user) => user.id === selectedValue);
                                        return user ? `${user.first_name} ${user.last_name}` : "ERROR";
                                    })
                                    .join(", ")
                            }
                            size="small"
                            fullWidth
                        >
                            {userSmallArray
                                .filter((user) => user.profile !== null)
                                .filter((user) => [1, 3, 4, 5, 6].includes(user.profile!.Role))
                                .sort((a, b) =>
                                    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
                                )
                                .map((user) => (
                                    <MenuItem key={`user-${user.id}`} value={user.id}>
                                        <Checkbox checked={selectedUser.includes(user.id)} />
                                        <ListItemText primary={`${user.first_name} ${user.last_name}`} />
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>

                {showTeamChart && (
                    <SalesBoardUser
                        key="salesboard-user-all"
                        isTeam={true}
                        onHide={handleHideTeamChart}
                        logArray={logArray || []}
                        projectStateArray={projectStateArray || []}
                        projectSmallArray={projectSmallArray || []}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                )}

                {userSmallArray
                    .filter((user) => user.profile !== null)
                    .filter((user) => selectedUser.includes(user.id))
                    .map((user) => (
                        <SalesBoardUser
                            key={`salesboard-user${user.id}`}
                            isTeam={false}
                            onHide={() => handleRemoveUser(user.id)}
                            currentUserSamll={user}
                            logArray={logArray || []}
                            projectStateArray={projectStateArray || []}
                            projectSmallArray={projectSmallArray || []}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    ))}
            </Grid>

            {showLeadChart && (
                <Card variant="outlined" sx={{ mt: 4 }}>
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="h6" gutterBottom>
                                Lead-Quellen der Projekte
                            </Typography>
                            <IconButton size="small" onClick={handleHideLeadChart}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={multiSeriesData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {uniqueLeadNames.map((lq, idx) => (
                                    <Line
                                        key={lq}
                                        type="monotone"
                                        dataKey={lq}
                                        name={lq}
                                        stroke={COLORS[idx % COLORS.length]}
                                        activeDot={{ r: 8 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            <Box sx={{ mb: 10 }} />
        </>
    );
};
